import {
  Badge,
  Box,
  Button,
  Card,
  HStack,
  NativeSelectField,
  NativeSelectRoot,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"

import type { QuestionFeedbackPublic } from "@/client"
import { FeedbackService, QuizzesService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { FeedbackModal } from "@/components/Feedback/FeedbackModal"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { useFeedbackEnabled } from "@/hooks/useFeedbackEnabled"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

export const Route = createFileRoute("/_layout/feedback")({
  validateSearch: (search: Record<string, unknown>) => ({
    feedbackId:
      typeof search.feedbackId === "string"
        ? (search.feedbackId as string)
        : undefined,
  }),
  component: FeedbackManagementPage,
})

function FeedbackManagementPage() {
  const queryClient = useQueryClient()
  const { feedbackId } = Route.useSearch()
  const breadcrumbs = useRouteBreadcrumbs("feedback.org")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [quizFilter, setQuizFilter] = useState<string>("all")
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [selectedFeedback, setSelectedFeedback] =
    useState<QuestionFeedbackPublic | null>(null)
  const { enabled: feedbackEnabled } = useFeedbackEnabled()

  // Get quizzes for filter
  const { data: quizzes } = useQuery({
    queryKey: ["quizzes"],
    queryFn: () => QuizzesService.listQuizzes({ skip: 0, limit: 100 }),
    enabled: feedbackEnabled,
  })

  // Get feedback (uses current org from session)
  const {
    data: feedbackList = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organizationFeedback", statusFilter, quizFilter],
    queryFn: () =>
      FeedbackService.getOrganizationFeedback({
        status: statusFilter === "all" ? undefined : statusFilter,
        quizId: quizFilter === "all" ? undefined : quizFilter,
      }),
    enabled: feedbackEnabled,
  })

  const handleFeedbackClick = (feedback: QuestionFeedbackPublic) => {
    setSelectedFeedback(feedback)
    setFeedbackModalOpen(true)
  }

  useEffect(() => {
    if (!feedbackEnabled) {
      return
    }
    if (!feedbackId || !feedbackList.length || feedbackModalOpen) {
      return
    }
    const match = feedbackList.find((feedback) => feedback.id === feedbackId)
    if (match) {
      setSelectedFeedback(match)
      setFeedbackModalOpen(true)
    }
  }, [feedbackEnabled, feedbackId, feedbackList, feedbackModalOpen])

  const getStatusBadge = (status: string) => {
    const colorMap: Record<string, string> = {
      unread: "red",
      read: "yellow",
      resolved: "green",
    }
    return (
      <Badge
        colorPalette={colorMap[status] || "gray"}
        textTransform="capitalize"
      >
        {status}
      </Badge>
    )
  }

  if (!feedbackEnabled) {
    return (
      <PageShell
        title="Manage Feedback"
        breadcrumbs={breadcrumbs}
        description="Feedback is disabled for your organization."
      >
        <Card.Root>
          <Card.Body>
            <Text color="fg.muted">
              This organization has disabled feedback.
            </Text>
          </Card.Body>
        </Card.Root>
      </PageShell>
    )
  }

  if (error) {
    return (
      <PageShell
        title="Manage Feedback"
        breadcrumbs={breadcrumbs}
        description="View and respond to student feedback across all quizzes."
      >
        <Text color="red.500">Error loading feedback. Please try again.</Text>
      </PageShell>
    )
  }

  return (
    <PageShell
      title="Manage Feedback"
      breadcrumbs={breadcrumbs}
      description="View and respond to student feedback across all quizzes."
    >
      <Stack gap={6}>
        {/* Filters */}
        <Card.Root p={4}>
          <HStack gap={4} wrap="wrap">
            <Box flex="1" minW="150px">
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Status
              </Text>
              <NativeSelectRoot>
                <NativeSelectField
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                  <option value="resolved">Resolved</option>
                </NativeSelectField>
              </NativeSelectRoot>
            </Box>

            <Box flex="1" minW="200px">
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Quiz
              </Text>
              <NativeSelectRoot>
                <NativeSelectField
                  value={quizFilter}
                  onChange={(e) => setQuizFilter(e.target.value)}
                >
                  <option value="all">All Quizzes</option>
                  {quizzes?.data?.map((quiz) => (
                    <option key={quiz.id} value={quiz.id}>
                      {quiz.name}
                    </option>
                  ))}
                </NativeSelectField>
              </NativeSelectRoot>
            </Box>
          </HStack>
        </Card.Root>

        <Card.Root>
          {isLoading ? (
            <PageSkeleton variant="table" rows={5} />
          ) : feedbackList.length === 0 ? (
            <Card.Body>
              <AppEmptyState
                title={emptyStateCopy.feedback.adminEmpty.title}
                description={emptyStateCopy.feedback.adminEmpty.description}
                variant="compact"
                data-testid="feedback-admin-empty-state"
              />
            </Card.Body>
          ) : (
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Quiz</Table.ColumnHeader>
                  <Table.ColumnHeader>Feedback</Table.ColumnHeader>
                  <Table.ColumnHeader>Student</Table.ColumnHeader>
                  <Table.ColumnHeader>Assigned To</Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                  <Table.ColumnHeader>Replies</Table.ColumnHeader>
                  <Table.ColumnHeader>Created</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {feedbackList.map((feedback) => {
                  const quiz = quizzes?.data?.find(
                    (q) => q.id === feedback.quiz_id,
                  )
                  return (
                    <Table.Row key={feedback.id}>
                      <Table.Cell>
                        <Text fontSize="sm" fontWeight="medium">
                          {quiz?.name || "Unknown"}
                        </Text>
                      </Table.Cell>
                      <Table.Cell maxW="300px">
                        <HtmlContent
                          content={feedback.feedback_text}
                          fontSize="sm"
                          maxH="3em"
                          overflow="hidden"
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">
                          {feedback.created_by_name || "Anonymous"}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">
                          {feedback.assigned_to_name || "-"}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>{getStatusBadge(feedback.status)}</Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">
                          {feedback.replies?.length || 0}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">
                          {new Date(feedback.created_at).toLocaleDateString()}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleFeedbackClick(feedback)}
                        >
                          View
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          )}
        </Card.Root>
      </Stack>

      {selectedFeedback && (
        <FeedbackModal
          open={feedbackModalOpen}
          onClose={() => {
            setFeedbackModalOpen(false)
            setSelectedFeedback(null)
            queryClient.invalidateQueries({
              queryKey: ["organizationFeedback"],
            })
          }}
          quizId={selectedFeedback.quiz_id}
          questionId={selectedFeedback.question_id}
          isTeacher={true}
        />
      )}
    </PageShell>
  )
}
